// Relative imports (not `@/`) so bun can test this module.
import { getHexTileMapKey } from '../hexmap/model/keys';
import type { RuleSet } from '../rules';
import {
  type Building,
  type Cell,
  type EntityId,
  type PlayerId,
  type SimContext,
  type SimState,
  territoryOwners,
  tileResources,
  type Unit,
  workerSlots,
} from '../sim';
import { assignedWorkers, type WorkerIndex } from './activity';
import { cityName } from './cities';
import { type StatusEnv, unitActivity, unitStatus } from './unitStatus';

/**
 * The Workers roster (left column): every own worker under the task it is on
 * (a farm, forestry or mine, a wild tile's resource, a site, hauling, idle),
 * built once per tick from the player's own units and buildings only, so
 * nothing in fog is described. Labels take the city numbers at render time:
 * the roster itself holds ids.
 */

type TaskKind = 'building' | 'site' | 'wild' | 'hauling' | 'other' | 'idle';

/** What a task is, shared by a group and a reassign-menu option. */
export interface TaskRef {
  /** `b:<id>` (building), `s:<id>` (site), `w:<resource>`, or the kind. */
  key: string;
  kind: TaskKind;
  /** The farm/forestry/mine or site (`building`, `site`): what `assign` takes as its target. */
  buildingId?: EntityId;
  /** "Farm", "Barracks site", "Wild food". */
  name: string;
  /** The city whose territory the building stands in. */
  cityId?: EntityId;
}

export interface RosterWorker {
  unit: Unit;
  /** The row's status text (UnitsPanel's). */
  status: string;
  pinned: boolean;
  /** The city whose territory its task is in (its home city when it has none). */
  workCityId?: EntityId;
}

export interface TaskGroup extends TaskRef {
  /** Workers on the building (inside it or on the road for it) out of its slots. */
  slots?: { used: number; total: number };
  workers: RosterWorker[];
}

export interface TaskOption extends TaskRef {
  slots?: { used: number; total: number };
  /** Why it can't take another worker, if so. */
  full: boolean;
}

export interface Roster {
  groups: TaskGroup[];
  /** Ids of workers with nothing to do (idle or no work found), in id order. */
  idle: EntityId[];
  options: TaskOption[];
  count: number;
}

const KIND_ORDER: TaskKind[] = ['building', 'wild', 'site', 'hauling', 'other', 'idle'];

/** The numeric part of an id (`u12` → 12), for ordering and the row's "Wk 12". */
const idNumbers = new Map<string, number>();
export function idNumber(id: string): number {
  let n = idNumbers.get(id);
  if (n === undefined) {
    n = Number.parseInt(id.replace(/\D/g, ''), 10) || 0;
    idNumbers.set(id, n);
  }
  return n;
}

function byId<T extends { id: string }>(a: T, b: T): number {
  return idNumber(a.id) - idNumber(b.id);
}

/** An own building's task, if it offers one: a site to build, or a providing building to work. */
function buildingTask(
  rules: RuleSet,
  owners: ReadonlyMap<string, EntityId>,
  b: Building,
  name: string
): TaskRef | null {
  const def = rules.buildings[b.type];
  if (!def) return null;
  const cityId = owners.get(getHexTileMapKey(b.pos));
  if (b.buildProgress < 1) {
    return def.consumesBuilder
      ? null
      : { key: `s:${b.id}`, kind: 'site', buildingId: b.id, name: `${def.name} site`, cityId };
  }
  return def.provides
    ? { key: `b:${b.id}`, kind: 'building', buildingId: b.id, name, cityId }
    : null;
}

export function buildRoster(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  env: StatusEnv,
  index: WorkerIndex
): Roster {
  const { rules } = ctx;
  const owners = territoryOwners(state, rules);
  const own = Object.values(state.buildings)
    .filter((b) => b.owner === playerId)
    .sort(byId);
  // Tile → own building covering it (its tile or a footprint tile).
  const cover = new Map<string, Building>();
  for (const b of own) {
    cover.set(getHexTileMapKey(b.pos), b);
    for (const cell of b.footprint ?? []) cover.set(getHexTileMapKey(cell), b);
  }

  // Every task an own building offers: "Farm", or "Farm 2" beside another in its city.
  const offered = new Map<string, TaskOption>();
  const perCity = new Map<string, number>();
  for (const b of own) {
    const def = rules.buildings[b.type];
    if (!def || b.buildProgress < 1 || !def.provides) continue;
    const k = `${b.type}@${owners.get(getHexTileMapKey(b.pos)) ?? ''}`;
    perCity.set(k, (perCity.get(k) ?? 0) + 1);
  }
  const seen = new Map<string, number>();
  for (const b of own) {
    const def = rules.buildings[b.type];
    if (!def) continue;
    let name = def.name;
    if (b.buildProgress >= 1 && def.provides) {
      const k = `${b.type}@${owners.get(getHexTileMapKey(b.pos)) ?? ''}`;
      const n = (seen.get(k) ?? 0) + 1;
      seen.set(k, n);
      if ((perCity.get(k) ?? 0) > 1) name = `${def.name} ${n}`;
    }
    const task = buildingTask(rules, owners, b, name);
    if (!task) continue;
    const total = task.kind === 'building' ? workerSlots(rules, b) : undefined;
    const used = task.kind === 'building' ? assignedWorkers(state, rules, b, index).total : 0;
    offered.set(task.key, {
      ...task,
      slots: total === undefined ? undefined : { used, total },
      full: total !== undefined && used >= total,
    });
  }

  function wildTask(cell: Cell): TaskRef | null {
    const res = tileResources(state, ctx, cell)?.resourceId;
    if (!res) return null;
    const name = rules.resources[res]?.name ?? res;
    return { key: `w:${res}`, kind: 'wild', name: `Wild ${name.toLowerCase()}` };
  }

  /** The task at a tile: the building on it, else its wild resource. */
  function taskAt(cell: Cell): { task: TaskRef; cell: Cell } | null {
    const b = cover.get(getHexTileMapKey(cell));
    if (b) {
      const task = offered.get(`b:${b.id}`) ?? offered.get(`s:${b.id}`);
      return task ? { task, cell: b.pos } : null;
    }
    const task = wildTask(cell);
    return task ? { task, cell } : null;
  }

  const plain = (kind: TaskKind, name: string): TaskRef => ({ key: kind, kind, name });
  const TASKS = {
    hauling: plain('hauling', 'Hauling'),
    other: plain('other', 'Other'),
    idle: plain('idle', 'Idle · no work found'),
  };

  const groups = new Map<string, TaskGroup>();
  const idle: Unit[] = [];
  let count = 0;
  for (const u of Object.values(state.units).sort(byId)) {
    if (u.owner !== playerId || rules.units[u.type]?.automate !== 'work') continue;
    count++;
    const activity = unitActivity(env, u);
    const order = u.order;
    let task: TaskRef = TASKS.other;
    let at: Cell = u.pos;
    if (order?.kind === 'build') {
      const found = state.buildings[order.buildingId];
      const t = found && offered.get(`s:${found.id}`);
      if (t) {
        task = t;
        at = found.pos;
      }
    } else if (
      activity === 'hauling' ||
      activity === 'unloading' ||
      (order?.kind === 'gather' && order.haul)
    ) {
      task = TASKS.hauling;
    } else {
      const inside = u.inside ? offered.get(`b:${u.inside}`) : undefined;
      const target =
        order?.kind === 'gather'
          ? order.target
          : activity === 'heading to work'
            ? u.auto?.target
            : activity === 'gathering' || activity === 'farming'
              ? u.pos
              : undefined;
      const found = inside
        ? { task: inside, cell: state.buildings[u.inside ?? ''].pos }
        : target && taskAt(target);
      if (found) {
        task = found.task;
        at = found.cell;
      } else if (activity === 'idle' || activity === 'no work found') {
        task = TASKS.idle;
      }
    }
    const pinned = order?.kind === 'gather' || order?.kind === 'build' ? order.pin === true : false;
    // A pinned worker standing with nothing to do (job full, unreachable or dry) is idle too.
    const stuck = pinned && (activity === 'idle' || activity === 'no work found');
    if (task.kind === 'idle' || stuck) idle.push(u);
    let group = groups.get(task.key);
    if (!group) {
      const offer = offered.get(task.key);
      group = { ...task, slots: offer?.slots, workers: [] };
      groups.set(task.key, group);
    }
    group.workers.push({
      unit: u,
      status: stuck ? 'pinned · not working' : unitStatus(env, u),
      pinned,
      workCityId: owners.get(getHexTileMapKey(at)) ?? u.homeCityId,
    });
  }

  const rank = (g: TaskRef) => KIND_ORDER.indexOf(g.kind);
  return {
    groups: [...groups.values()].sort(
      (a, b) =>
        rank(a) - rank(b) ||
        idNumber(a.buildingId ?? '') - idNumber(b.buildingId ?? '') ||
        a.name.localeCompare(b.name)
    ),
    idle: idle.map((u) => u.id),
    options: [...offered.values()],
    count,
  };
}

/** A group or menu entry's text: "Farm · City 1", "Wild food", "Barracks site · City 1". */
export function taskLabel(
  task: TaskRef,
  playerId: PlayerId,
  cityNumbers: ReadonlyMap<string, number>,
  state: SimState
): string {
  const city = task.cityId ? state.buildings[task.cityId] : undefined;
  const where = city ? ` · ${cityName(city, playerId, cityNumbers)}` : '';
  return task.kind === 'idle' ? task.name : `${task.name}${where}`;
}

/** A group header: "Building · " ahead of a site's name. */
export function groupTitle(
  group: TaskGroup,
  playerId: PlayerId,
  cityNumbers: ReadonlyMap<string, number>,
  state: SimState
): string {
  const label = taskLabel(group, playerId, cityNumbers, state);
  return group.kind === 'site' ? `Building · ${label}` : label;
}

/** "C1": a city's short chip, "—" when it has no number. */
export function cityTag(cityNumbers: ReadonlyMap<string, number>, cityId: EntityId | undefined) {
  const n = cityId === undefined ? undefined : cityNumbers.get(cityId);
  return n === undefined ? '—' : `C${n}`;
}

/** "3/5" for a task with slots, else "". */
export function slotText(slots: { used: number; total: number } | undefined): string {
  return slots ? `${slots.used}/${slots.total}` : '';
}

/**
 * The idle worker `.` goes to next: the one after the selected one in `idle`
 * (wrapping) when exactly one idle worker is selected, else the first.
 * Null with nobody idle.
 */
export function nextIdle(
  idle: readonly EntityId[],
  selected: readonly EntityId[]
): EntityId | null {
  if (idle.length === 0) return null;
  const at = selected.length === 1 ? idle.indexOf(selected[0]) : -1;
  return idle[(at + 1) % idle.length];
}
